<template>
  <div>
    <h4 class="text-center">Mobile channel</h4>
    <p class="mt-3 text-center text-muted">
      Mobile channel allows you to send and receive SMS with single API.
      Amio integrates with <a href="https://messagebird.com/" target="_blank">MessageBird</a>, allowing you to send
      messages around the whole world.
    </p>
    <div>
      <form @submit.prevent="promiseConnectAccessToken = connectProvider()">
        <div class="form-group row">
          <label class="col-5 col-form-label text-muted">Provider:</label>
          <div class="col-7 my-auto">
            <span>
              <img width="24px" class="rounded m-0" :src="messageBirdLogo" alt="MessageBird"/>
              MessageBird
            </span>
          </div>
        </div>
        <div class="form-group row">
          <label class="col-5 text-muted col-form-label text-muted" for="access-token-input">
            Access Key:
            <span id="access-key-info" v-b-popover:access-key-info.html.hover.right="accessKeyPopover">
              <icon name="info-circle"></icon>
            </span>
          </label>
          <div class="col-7 my-auto">
            <input id="access-token-input"
                   class="form-control"
                   :class="{'is-invalid': accessTokenErrorMessage}"
                   v-model="accessToken"/>
            <div v-show="accessTokenErrorMessage" class="invalid-feedback">
              {{ accessTokenErrorMessage }}
            </div>
          </div>
        </div>

        <div class="form-group row">
          <label class="col-5 text-muted col-form-label text-muted" for="signing-key-input">
            Signing Key:
            <span id="signing-key-info" v-b-popover:signing-key-info.html.hover.right="signingKeyPopover">
              <icon name="info-circle"></icon>
            </span>
          </label>
          <div class="col-7 my-auto">
            <input id="signing-key-input"
                   class="form-control"
                   :class="{'is-invalid': signingKeyErrorMessage}"
                   v-model="signingKey"/>
            <div v-show="signingKeyErrorMessage" class="invalid-feedback">
              {{ signingKeyErrorMessage }}
            </div>
          </div>
        </div>

        <div class="form-group row">
          <label class="col-5 col-form-label text-muted" for="custom-number-checkbox">
            Use Custom Sender ID:
            <span id="custom-number-info" v-b-popover:custom-number-info.html.hover.right="customNumberPopover">
              <icon name="info-circle"></icon>
            </span>
          </label>
          <div class="col-7 my-auto">
            <input type="checkbox" id="custom-number-checkbox" v-model="useCustomNumber">
          </div>
        </div>
        <div class="form-group row">
          <label class="col-5 col-form-label text-muted" for="custom-number-input" :hidden="!useCustomNumber">
            Sender ID:
          </label>
          <div class="col-7 my-auto" :hidden="!useCustomNumber">
            <input class="form-control"
                   :class="{'is-invalid': customNumberErrorMessage}"
                   id="custom-number-input"
                   v-model="customNumber">
            <div v-show="customNumberErrorMessage" class="invalid-feedback">
              {{ customNumberErrorMessage }}
            </div>
          </div>
        </div>

        <div class="row mt-5">
          <div class="col-12 d-flex justify-content-center">
            <button class="btn btn-primary px-5"
                    v-busy-button="promiseConnectAccessToken">
              Connect
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
  import channelService from '@/services/amio-api/channel.service'
  import messageBirdLogo from '@/assets/img/logo/channels/mobile/messagebird.svg'
  import 'vue-awesome/icons/info-circle'

  const accessKeyPopover = 'Create new access key in <a href="https://dashboard.messagebird.com/en/developers/access" target="_blank">MessageBird administration</a> and copy-paste it here.'
  const customNumberPopover = 'This allows you to pick a custom <a href="https://support.messagebird.com/hc/en-us/articles/115002628665-What-is-the-Originator-" target="_blank">Originator</a> of sent messages. ' +
    'Please note that some countries apply <a href="https://support.messagebird.com/hc/en-us/sections/202017565-Country-Info-Restrictions" target="_blank">restrictions</a> and may overwrite this.'
  const signingKeyPopover = 'You can find your Signing Key in <a href="https://dashboard.messagebird.com/en/developers/settings" target="_blank">MessageBird administration</a>.'

  export default {
    data() {
      return {
        promiseConnectAccessToken: null,
        accessKeyPopover,
        customNumberPopover,
        signingKeyPopover,
        accessToken: '',
        accessTokenErrorMessage: '',
        useCustomNumber: true,
        customNumber: '',
        customNumberErrorMessage: '',
        signingKey: '',
        signingKeyErrorMessage: '',
        messageBirdLogo
      }
    },

    methods: {
      connectProvider() {
        if (!this.checkFormErrors()) return

        const data = {
          type: 'mobile',
          provider: {
            type: 'messagebird',
            sender_id: this.useCustomNumber ? this.customNumber : 'SMSInfo',
            access_token: this.accessToken,
            signing_key: this.signingKey
          }
        }
        return channelService.create(data, [422])
          .then(channel => {
            this.channel = channel
            this.$router.push({
              name: 'administration.channel-detail.info',
              params: {id: channel.id},
              query: {showSetup: 'true'}
            })
          })
          .catch((err) => {
            if (err.response.status !== 422) return

            const error = err.response.data.errors ? err.response.data.errors[0] : {}
            return this.handleError(error)
          })
          .then(() => {
            this.promiseConnectAccessToken = null
          })
      },

      clearError() {
        this.accessTokenErrorMessage = ''
        this.customNumberErrorMessage = ''
        this.signingKeyErrorMessage = ''
      },

      handleError(error) {
        if (error.field === 'provider.sender_id') {
          this.customNumberErrorMessage = error.message
        } else if (error.field === 'provider.signing_key') {
          this.signingKeyErrorMessage = error.message
        } else {
          this.accessTokenErrorMessage = error.message
        }
      },

      checkFormErrors() {
        this.clearError()

        if (!this.accessToken) {
          this.accessTokenErrorMessage = 'Please provide an Access Key.'
        }

        if (this.useCustomNumber && !this.customNumber) {
          this.customNumberErrorMessage = 'Please provide a Sender ID.'
        }

        if (!this.signingKey) {
          this.signingKeyErrorMessage = 'Please provide Signing Key.'
        }

        return !this.accessTokenErrorMessage && !this.customNumberErrorMessage && !this.signingKeyErrorMessage
      }
    }
  }
</script>
